import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function waitForImages(root: HTMLElement, timeoutMs = 4000): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();

  return Promise.race([
    Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            const done = () => resolve();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
          })
      )
    ).then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

type PrintFrame = { iframe: HTMLIFrameElement; blobUrl: string };

/**
 * Chrome's print() on a PDF iframe returns before the print preview has been
 * built, so the frame must outlive the call: removing it (or revoking its blob)
 * while the dialog is loading leaves the preview blank or closes it. The last
 * frame is therefore kept alive and only disposed when the next print starts.
 */
let activePrintFrame: PrintFrame | null = null;

function disposePrintFrame(frame: PrintFrame | null) {
  if (!frame) return;
  frame.iframe.remove();
  URL.revokeObjectURL(frame.blobUrl);
  if (activePrintFrame === frame) activePrintFrame = null;
}

function triggerPrintFromBlob(blobUrl: string): Promise<void> {
  disposePrintFrame(activePrintFrame);

  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const frame: PrintFrame = { iframe, blobUrl };
    activePrintFrame = frame;

    let printed = false;
    const doPrint = () => {
      // onload and the fallback timer can both fire; print exactly once.
      if (printed) return;
      printed = true;
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        disposePrintFrame(frame);
        reject(err);
        return;
      }
      resolve();
    };

    iframe.onload = () => setTimeout(doPrint, 300);
    iframe.src = blobUrl;
    setTimeout(doPrint, 1500);
  });
}

export type PrintElementOptions = {
  contentSelector?: string;
  /** PDF page width in mm (default 210 = A4). Use 80 for thermal token printers. */
  paperWidthMm?: number;
};

export async function printElementToPdf(
  element: HTMLElement,
  options?: PrintElementOptions
): Promise<void> {
  const paperWidthMm = options?.paperWidthMm ?? 210;
  const target =
    (options?.contentSelector
      ? (element.querySelector(options.contentSelector) as HTMLElement | null)
      : null) ?? element;

  if (!target) {
    throw new Error("Print target not found");
  }

  const previousTargetDisplay = target.style.display;
  const previousTargetVisibility = target.style.visibility;
  const previousTargetTransform = target.style.transform;

  target.style.display = "block";
  target.style.visibility = "visible";
  target.style.transform = "none";

  try {
    await waitForImages(target);
    await waitForNextPaint();

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Print preview is empty");
    }

    const imgData = canvas.toDataURL("image/png");
    const imgHeight = (canvas.height / canvas.width) * paperWidthMm;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [paperWidthMm, Math.max(imgHeight, paperWidthMm < 80 ? 20 : 40)],
    });

    pdf.addImage(imgData, "PNG", 0, 0, paperWidthMm, imgHeight, "", "FAST");

    const pdfBlob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    await triggerPrintFromBlob(blobUrl);
  } finally {
    target.style.display = previousTargetDisplay;
    target.style.visibility = previousTargetVisibility;
    target.style.transform = previousTargetTransform;
  }
}
